/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * Enhanced with support for:
 * - Keywords
 * - Canonical URLs
 * - Open Graph images
 * - Twitter cards
 * - Robots meta tags
 * - Structured data (JSON-LD)
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */
import * as React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

function Seo({
	description,
	lang,
	meta,
	title,
	keywords,
	canonical,
	ogImage,
	ogType,
	twitterCard,
	twitterSite,
	robots,
	structuredData,
	noindex,
	nofollow,
}) {
	const { site } = useStaticQuery(
		graphql`
			query {
				site {
					siteMetadata {
						title
						description
						author
						siteUrl
					}
				}
			}
		`
	)

	const metaDescription = description || site.siteMetadata.description
	const defaultTitle = site.siteMetadata?.title
	const siteUrl = site.siteMetadata?.siteUrl || `https://ipweansw.org/`

	// Build canonical URL
	const canonicalUrl = canonical
		? canonical.startsWith("http")
			? canonical
			: `${siteUrl}${canonical.replace(/^\//, "")}`
		: typeof window !== "undefined"
		? window.location.href
		: siteUrl

	// Build OG image URL
	const ogImageUrl = ogImage
		? ogImage.startsWith("http")
			? ogImage
			: `${siteUrl}${ogImage.replace(/^\//, "")}`
		: null

	// Build robots meta
	const robotsContent = robots || [
		noindex ? "noindex" : "index",
		nofollow ? "nofollow" : "follow",
	].join(", ")

	// Build meta tags array
	const metaTags = [
		{
			name: `description`,
			content: metaDescription,
		},
		{
			property: `og:title`,
			content: title,
		},
		{
			property: `og:description`,
			content: metaDescription,
		},
		{
			property: `og:type`,
			content: ogType || `website`,
		},
		{
			property: `og:url`,
			content: canonicalUrl,
		},
		{
			name: `twitter:card`,
			content: twitterCard || `summary_large_image`,
		},
		{
			name: `twitter:title`,
			content: title,
		},
		{
			name: `twitter:description`,
			content: metaDescription,
		},
	]

	// Add keywords if provided
	if (keywords) {
		metaTags.push({
			name: `keywords`,
			content: Array.isArray(keywords) ? keywords.join(`, `) : keywords,
		})
	}

	// Add OG image if provided
	if (ogImageUrl) {
		metaTags.push({
			property: `og:image`,
			content: ogImageUrl,
		})
		metaTags.push({
			name: `twitter:image`,
			content: ogImageUrl,
		})
	}

	// Add Twitter site if provided
	if (twitterSite) {
		metaTags.push({
			name: `twitter:site`,
			content: twitterSite,
		})
	}

	// Add robots meta
	if (robotsContent) {
		metaTags.push({
			name: `robots`,
			content: robotsContent,
		})
	}

	// Add Facebook domain verification
	metaTags.push({
		name: `facebook-domain-verification`,
		content: `7t7y3oyngazf7v96lsluxs7jb571bg`,
	})

	// Add author if available
	if (site.siteMetadata?.author) {
		metaTags.push({
			name: `author`,
			content: site.siteMetadata.author,
		})
		metaTags.push({
			name: `twitter:creator`,
			content: site.siteMetadata.author,
		})
	}

	return (
		<Helmet>
			<html lang={lang} />
			<title>
				{defaultTitle ? `${title} | ${defaultTitle}` : title}
			</title>
			{canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
			{metaTags.concat(meta).map((tag, index) => {
				if (tag.property) {
					return (
						<meta
							key={`meta-og-${index}`}
							property={tag.property}
							content={tag.content}
						/>
					)
				}
				return (
					<meta
						key={`meta-${index}`}
						name={tag.name}
						content={tag.content}
					/>
				)
			})}
			{structuredData && (
				<script type="application/ld+json">
					{JSON.stringify(structuredData)}
				</script>
			)}
		</Helmet>
	)
}

Seo.defaultProps = {
	lang: `en`,
	meta: [],
	description: ``,
	keywords: null,
	canonical: null,
	ogImage: null,
	ogType: `website`,
	twitterCard: `summary_large_image`,
	twitterSite: null,
	robots: null,
	structuredData: null,
	noindex: false,
	nofollow: false,
}

Seo.propTypes = {
	description: PropTypes.string,
	lang: PropTypes.string,
	meta: PropTypes.arrayOf(PropTypes.object),
	title: PropTypes.string.isRequired,
	keywords: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
	canonical: PropTypes.string,
	ogImage: PropTypes.string,
	ogType: PropTypes.string,
	twitterCard: PropTypes.string,
	twitterSite: PropTypes.string,
	robots: PropTypes.string,
	structuredData: PropTypes.object,
	noindex: PropTypes.bool,
	nofollow: PropTypes.bool,
}

export default Seo
